import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  LockKey, Eye, EyeSlash, User, IdentificationBadge, Bell, BellSlash,
} from "@phosphor-icons/react";
import {
  isPushSupported, getCurrentSubscription, subscribeToPush,
  unsubscribeFromPush, sendTestPush,
} from "../lib/push";

const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

const CATEGORIES = [
  { key: "registration", label: "New player registrations", description: "Get pinged when a family signs their child up." },
  { key: "announcement", label: "Announcements & replies", description: "Coach announcements and comment activity." },
  { key: "reminder", label: "Practice & match reminders", description: "Upcoming sessions, matches, and birthdays." },
];

export default function Profile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [prefs, setPrefs] = useState(null);
  const [pushSub, setPushSub] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const pushSupported = isPushSupported();

  useEffect(() => {
    api.get("/notifications/prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({}));
    if (pushSupported) getCurrentSubscription().then(setPushSub).catch(() => {});
  }, [pushSupported]);

  async function savePassword(e) {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  async function togglePref(key, value) {
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const r = await api.put("/notifications/prefs", { [key]: value });
      setPrefs(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function enablePush() {
    setPushBusy(true);
    try {
      await subscribeToPush();
      const sub = await getCurrentSubscription();
      setPushSub(sub);
      toast.success("Push notifications enabled");
    } catch (err) { toast.error(err.message || "Could not enable push"); }
    finally { setPushBusy(false); }
  }

  async function disablePush() {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setPushSub(null);
      toast.success("Push notifications disabled");
    } catch (err) { toast.error(err.message || "Could not disable push"); }
    finally { setPushBusy(false); }
  }

  async function doTestPush() {
    try { await sendTestPush(); toast.success("Test push sent — check your device"); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  const canManagePrefs = user?.role === "admin" || user?.role === "coach";

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="overline">Account</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Profile</h1>
      </div>

      <section className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-3">
        <div className="overline flex items-center gap-2"><IdentificationBadge size={14} weight="bold" /> You</div>
        <Row icon={User} label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={(user?.role || "").toUpperCase()} />
      </section>

      {canManagePrefs && (
        <section className="bg-[#121212] border border-neutral-800 rounded-lg p-6 space-y-5" data-testid="prefs-section">
          <div className="overline flex items-center gap-2"><Bell size={14} weight="bold" /> Notification Preferences</div>
          <div className="text-xs text-neutral-500">Choose which alerts show up in your bell.</div>
          <div className="space-y-3">
            {CATEGORIES.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-4 py-2 border-b border-neutral-900 last:border-0">
                <div>
                  <div className="text-sm font-semibold">{c.label}</div>
                  <div className="text-xs text-neutral-500">{c.description}</div>
                </div>
                <Toggle
                  testid={`pref-${c.key}`}
                  checked={!!(prefs && prefs[c.key])}
                  disabled={!prefs}
                  onChange={(v) => togglePref(c.key, v)}
                />
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-neutral-900 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  {pushSub ? <Bell size={14} weight="fill" /> : <BellSlash size={14} weight="bold" />}
                  Push notifications
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {pushSupported
                    ? "Get alerts on this device even when the app is closed."
                    : "This browser does not support push notifications."}
                </div>
              </div>
              {pushSupported && (
                pushSub ? (
                  <button
                    onClick={disablePush}
                    disabled={pushBusy}
                    data-testid="push-disable"
                    className="h-10 px-4 rounded-md border border-neutral-800 text-neutral-300 hover:border-[#D90429] hover:text-white text-xs font-bold uppercase tracking-wider disabled:opacity-60"
                  >
                    {pushBusy ? "Working…" : "Turn off"}
                  </button>
                ) : (
                  <button
                    onClick={enablePush}
                    disabled={pushBusy}
                    data-testid="push-enable"
                    className="h-10 px-4 rounded-md bg-[#06D6A0] hover:bg-[#04b587] text-[#050505] text-xs font-black uppercase tracking-wider disabled:opacity-60"
                  >
                    {pushBusy ? "Working…" : "Turn on"}
                  </button>
                )
              )}
            </div>
            {pushSub && (
              <button
                onClick={doTestPush}
                data-testid="push-test"
                className="text-xs font-bold uppercase tracking-wider text-[#06D6A0] hover:text-white"
              >
                Send test push →
              </button>
            )}
          </div>
        </section>
      )}

      <section className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
        <div className="overline flex items-center gap-2 mb-4"><LockKey size={14} weight="bold" /> Change Password</div>
        <form onSubmit={savePassword} className="space-y-4">
          <Field label="Current Password">
            <div className="relative">
              <input required data-testid="profile-current-pw" type={showCur ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} className={`${inputCls} pr-11`} autoComplete="current-password" />
              <ToggleEye show={showCur} onToggle={() => setShowCur((v) => !v)} testid="toggle-current" />
            </div>
          </Field>
          <Field label="New Password (min 6 chars)">
            <div className="relative">
              <input required data-testid="profile-new-pw" type={showNew ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} className={`${inputCls} pr-11`} autoComplete="new-password" />
              <ToggleEye show={showNew} onToggle={() => setShowNew((v) => !v)} testid="toggle-new" />
            </div>
          </Field>
          <Field label="Confirm New Password">
            <input required data-testid="profile-confirm-pw" type={showNew ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" />
          </Field>
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save-pw"
            className="h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider disabled:opacity-60"
          >
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </section>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${
        checked ? "bg-[#06D6A0]" : "bg-neutral-800"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <span
        className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-900 pb-2 gap-4">
      <div className="text-sm text-neutral-500 flex items-center gap-2">{Icon && <Icon size={14} weight="bold" />} {label}</div>
      <div className="text-sm font-semibold text-right">{value || "—"}</div>
    </div>
  );
}

function ToggleEye({ show, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      aria-label={show ? "Hide password" : "Show password"}
      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-white"
    >
      {show ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
    </button>
  );
}
